import { FC, useMemo, useState } from "react";
import { Redirect, useParams } from "react-router";
import {
    IonButton,
    IonContent,
    IonPage,
    IonSpinner,
    useIonActionSheet,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import {
    GpsCalibrationPoints,
    GpsPoint,
    MatchPhone,
    Point,
    VideoCalibration,
} from "refsix-js-models";
import {
    binaryIndexOf,
    eventComparator,
    generatePlottingDataForSegments,
    getPlayingSegments,
    matchEventsPosition,
    SpeedCategoryConstants,
} from "refsix-core";
import HeaderComponent from "../../components/HeaderComponent";
import {
    useDimensions,
    useFitnessDataContext,
    useProfile,
    useSettings,
} from "../../components/hooks";
import { FitnessDataProvider } from "../../components/hooks/fitnessData";
import { useMatch } from "../../components/hooks/match";
import { updateMatch } from "../../services/matchService";
import { setCalibrationPoints } from "../../services/fitnessDataSync/fitnessDataSyncService";
import { hasFeatureAccess } from "../../services/subscriptionService";
import { routes } from "../../route/constants";
import { HeartRateFilter } from "../../utils/filters/HeartRateFilter";
import { LiveGraphConfig, ReplayTick, SeekCommand, VideoDetails } from "./replayTypes";
import {
    matchTime as formatMatchTime,
    processReplayEvents,
    resolveSeekTarget,
    speedMSToKmH,
} from "./replayUtils";
import pitchImage from "../../assets/images/pitch.png";
import VideoFilePicker from "./components/videoFilePicker";
import VideoCalibrate from "./components/videoCalibrate";
import VideoPlayer from "./components/videoPlayer";
import ReplayPitchPosition from "./components/replayPitchPosition";
import ReplayHeatmap from "./components/replayHeatmap";
import ReplayCardmap from "./components/replayCardmap";
import ReplayLiveGraph from "./components/replayLiveGraph";
import MatchEventLog from "../../components/match/MatchEventLog";
import { DisplayEvent } from "../../models/DisplayEvent";
import "./replay.css";

const DEFAULT_MAX_SPEED = 1;

const ReplayContent: FC<{ match: MatchPhone }> = ({ match }) => {
    const { t } = useTranslation();
    const profile = useProfile();
    const settings = useSettings();
    const useImperial = !!settings?.useImperial;
    const { gpsData, hrData } = useFitnessDataContext();
    const [presentActionSheet] = useIonActionSheet();
    const [containerRef, dims] = useDimensions();

    const matchTimings = useMemo(() => getPlayingSegments(match), [match]);
    const processedEvents = useMemo(
        () => processReplayEvents(match.matchEvents),
        [match.matchEvents]
    );

    // Local override so a heatmap flip is reflected immediately before the
    // re-fetched, persisted calibration points come back.
    const [calibrationOverride, setCalibrationOverride] =
        useState<GpsCalibrationPoints | null>(null);
    const calibrationPoints =
        calibrationOverride || gpsData?.calibrationPoints || null;

    // Available space for the heatmap (measured from its frame).
    const frameW = Math.max(0, (dims?.width || 0) - 10);
    const frameH = Math.max(0, dims?.height || 0);

    const gpsPlottingData = useMemo(() => {
        if (!gpsData?.geoPoints || !calibrationPoints || !matchTimings || !frameW) {
            return null;
        }
        // First pass at the full frame width.
        const byWidth = generatePlottingDataForSegments(
            gpsData.geoPoints,
            calibrationPoints,
            matchTimings,
            frameW
        );
        const pd = byWidth?.[0]?.plottingData;
        // If the pitch-ratio height overflows the frame, re-plot to fit the height
        // so the whole map (and the graphs matched to it) stay on screen.
        if (pd && pd.width && pd.height && frameH && pd.height > frameH) {
            const fitWidth = Math.floor(frameH * (pd.width / pd.height));
            if (fitWidth > 0) {
                return generatePlottingDataForSegments(
                    gpsData.geoPoints,
                    calibrationPoints,
                    matchTimings,
                    fitWidth
                );
            }
        }
        return byWidth;
    }, [gpsData?.geoPoints, calibrationPoints, matchTimings, frameW, frameH]);

    const plottingDataSegment = gpsPlottingData?.[0]?.plottingData || null;
    // Match the live graphs to the heatmap (pitch) height so the three columns align.
    const mapHeight = plottingDataSegment
        ? Math.ceil(plottingDataSegment.height)
        : undefined;

    const cardMapData = useMemo(() => {
        if (
            gpsData?.geoPoints &&
            calibrationPoints &&
            matchTimings &&
            plottingDataSegment
        ) {
            return matchEventsPosition(
                gpsData.geoPoints,
                calibrationPoints,
                matchTimings,
                match.matchEvents,
                plottingDataSegment.width,
                gpsPlottingData
            );
        }
        return undefined;
    }, [
        gpsData?.geoPoints,
        calibrationPoints,
        matchTimings,
        plottingDataSegment,
        gpsPlottingData,
        match.matchEvents,
    ]);

    // ----- Video + playback state ----------------------------------------
    const [videoDetails, setVideoDetails] = useState<VideoDetails>(() => {
        const cal = match.videoCalibration;
        return {
            url: "",
            isCalibrated: cal?.isCalibrated || false,
            playSegmentOffsets:
                cal?.isCalibrated && cal.playSegments ? cal.playSegments : [],
        };
    });
    const [requestFileNonce, setRequestFileNonce] = useState(0);
    const [seekCommand, setSeekCommand] = useState<SeekCommand | null>(null);
    const [tick, setTick] = useState<ReplayTick | null>(null);
    const [playing, setPlaying] = useState(false);

    const currentTimestamp =
        tick?.timestamp || matchTimings[0]?.timestamp || 0;

    // Nearest GPS plotting point to the current playback timestamp.
    const { currentGPSPoint, currentGPSIndex } = useMemo(() => {
        if (!plottingDataSegment?.data?.length) {
            return { currentGPSPoint: undefined, currentGPSIndex: 0 };
        }
        // binaryIndexOf returns ~insertionPoint on a no-match; Math.abs maps that
        // to the neighbouring index, which can land one past the last sample when
        // playback runs beyond the final GPS point. Clamp to the last valid index.
        const index = Math.min(
            Math.abs(
                binaryIndexOf(
                    plottingDataSegment.data,
                    currentTimestamp,
                    eventComparator
                )
            ),
            plottingDataSegment.data.length - 1
        );
        return {
            currentGPSPoint: plottingDataSegment.data[index] as Point,
            currentGPSIndex: index,
        };
    }, [plottingDataSegment, currentTimestamp]);

    // ----- Live graph configs --------------------------------------------
    const maxSpeed = useMemo(() => {
        const points = (gpsData?.geoPoints as GpsPoint[]) || [];
        return points.reduce(
            (acc, p) => (acc > (p.speed || 0) ? acc : (p.speed || 0) + 1),
            DEFAULT_MAX_SPEED
        );
    }, [gpsData?.geoPoints]);

    const speedYAxisLabel = (speed: number): string | null => {
        let out: string | null = null;
        Object.keys(SpeedCategoryConstants).forEach((category) => {
            const cat = (SpeedCategoryConstants as any)[category];
            if (
                category !== "stand" &&
                category !== "sprint" &&
                cat.min === speed
            ) {
                out = category === "fastRun" ? "sprint" : category;
            }
        });
        return out !== null ? t("general." + out) : out;
    };

    const liveSpeedConfig: LiveGraphConfig = {
        lineColor: "#d5e830",
        lastPoints: 30,
        maxRepeatSeconds: 5,
        propertyToDisplay: "speed",
        min: 0,
        max: maxSpeed,
        stepSize: 0.01,
        decimalPlaces: 2,
        displayValue: (v) => speedMSToKmH(v, useImperial),
        yAxisLabel: speedYAxisLabel,
    };

    const liveHeartRateConfig: LiveGraphConfig = {
        lineColor: "#ff6384",
        lastPoints: 30,
        propertyToDisplay: "value",
        min: 60,
        max: 220,
        decimalPlaces: 0,
        displayValue: (v) => HeartRateFilter(v),
    };

    // ----- Handlers ------------------------------------------------------
    const onVideoSelected = (details: VideoDetails) => setVideoDetails(details);

    const onRequestFile = () => setRequestFileNonce((n) => n + 1);

    const onCalibrated = (details: VideoDetails) => {
        setVideoDetails(details);
        const videoCalibration: VideoCalibration = {
            isCalibrated: true,
            playSegments: details.playSegmentOffsets,
        };
        updateMatch({ ...match, videoCalibration }).catch((err) =>
            console.warn("Replay: failed to save video calibration", err)
        );
    };

    // Seek the video to a tapped event in the match log. resolveSeekTarget maps
    // the display event's timestamp + period to a playing-segment index and an
    // in-half offset (end-of-half rows seek to the end of the half); it returns
    // null for non-playable rows (half-time / breaks), which we ignore.
    const onSeek = (displayEvent: DisplayEvent) => {
        const target = resolveSeekTarget(displayEvent, matchTimings);
        if (!target) {
            return;
        }
        setSeekCommand({
            position: target.position,
            segmentIndex: target.segmentIndex,
            type: displayEvent.event.eventName,
            nonce: Date.now(),
        });
    };

    const editVideoCalibration = () =>
        setVideoDetails((vd) => ({ ...vd, isCalibrated: false }));

    const cancelEditCalibration = () =>
        setVideoDetails((vd) => ({ ...vd, isCalibrated: true }));

    const canCancelEditCalibration =
        !videoDetails.isCalibrated &&
        videoDetails.playSegmentOffsets.length === matchTimings.length;

    const editVideo = () => {
        presentActionSheet({
            header: t("general.whatToEdit"),
            buttons: [
                {
                    text: t("performance.Replay.calibration"),
                    handler: editVideoCalibration,
                },
                {
                    text: t("performance.Replay.video"),
                    handler: onRequestFile,
                },
                { text: t("general.cancel"), role: "cancel" },
            ],
        });
    };

    const flipHeatMap = () => {
        if (!calibrationPoints) {
            return;
        }
        const flipped: GpsCalibrationPoints = {
            ...calibrationPoints,
            flip: !calibrationPoints.flip,
        };
        setCalibrationOverride(flipped);
        if (profile?.username) {
            setCalibrationPoints(match._id || "", profile.username, flipped).catch(
                (err) =>
                    console.warn(
                        "Replay: unable to save flipped calibration points",
                        err
                    )
            );
        }
    };

    // Header end button toggles between Edit (when calibrated) and Cancel.
    const headerEndText = videoDetails.isCalibrated
        ? t("general.edit")
        : canCancelEditCalibration
        ? t("general.cancel")
        : undefined;
    const onClickEndButton = videoDetails.isCalibrated
        ? editVideo
        : cancelEditCalibration;

    return (
        <IonPage>
            <HeaderComponent
                title={t("fixture.FixtureSummary.replayTool")}
                showBackButton={true}
                headerEndText={headerEndText}
                onClickEndButton={onClickEndButton}
            />
            <IonContent className="replay-ioncontent">
                <VideoFilePicker
                    videoDetails={videoDetails}
                    onVideoSelected={onVideoSelected}
                    requestNonce={requestFileNonce}
                />

                {!videoDetails.isCalibrated ? (
                    <VideoCalibrate
                        matchTimings={matchTimings}
                        videoDetails={videoDetails}
                        onCalibrated={onCalibrated}
                        onRequestFile={onRequestFile}
                    />
                ) : (
                    <div className="replay-content">
                        {/* On portrait this stays frozen at the top while the
                            match log + perf widgets scroll beneath it (see
                            .video-section-player sticky rule in replay.css). */}
                        <div className="video-section-player">
                            <VideoPlayer
                                match={match}
                                matchTimings={matchTimings}
                                matchEvents={processedEvents}
                                videoDetails={videoDetails}
                                seekCommand={seekCommand}
                                currentTimestamp={currentTimestamp}
                                onTick={setTick}
                                onPlayStateChange={setPlaying}
                                onRequestFile={onRequestFile}
                            />
                        </div>
                        <div className="video-section-events">
                            <MatchEventLog
                                match={match}
                                order="asc"
                                onEventClick={onSeek}
                            />
                        </div>

                        <div className="perf-stats">
                            <div
                                className="results-group"
                                id="live-heatmap-container"
                            >
                                <div className="double">
                                    <h2>{t("general.heatmap")}</h2>
                                    <IonButton
                                        fill="clear"
                                        size="small"
                                        className="flip-heatmap-link"
                                        onClick={flipHeatMap}
                                    >
                                        {t("performance.Replay.flipHeatmap")}
                                    </IonButton>
                                </div>
                                <div
                                    className="heatmap-frame"
                                    ref={containerRef}
                                >
                                    {plottingDataSegment && (
                                        <div
                                            className="live-heatmap"
                                            style={{
                                                width: plottingDataSegment.width,
                                                height: Math.ceil(
                                                    plottingDataSegment.height
                                                ),
                                            }}
                                        >
                                            {/* Layers, bottom to top: pitch → heatmap → cards → position dot */}
                                            <img
                                                src={pitchImage}
                                                alt=""
                                                className="replay-pitch-base"
                                            />
                                            <ReplayHeatmap
                                                plottingData={
                                                    plottingDataSegment
                                                }
                                                currentIndex={currentGPSIndex}
                                            />
                                            {cardMapData && (
                                                <ReplayCardmap
                                                    cardMapData={cardMapData}
                                                    currentTimestamp={
                                                        currentTimestamp
                                                    }
                                                />
                                            )}
                                            <ReplayPitchPosition
                                                currentGPSPoint={
                                                    currentGPSPoint
                                                }
                                                playing={playing}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="results-group">
                                <h2>{t("general.speed")}</h2>
                                <div className="graph-frame">
                                    {gpsData?.geoPoints && (
                                        <ReplayLiveGraph
                                            points={gpsData.geoPoints}
                                            matchTimings={matchTimings}
                                            config={liveSpeedConfig}
                                            currentTimestamp={currentTimestamp}
                                            height={mapHeight}
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="results-group">
                                <h2>{t("general.heartRate")}</h2>
                                <div className="graph-frame">
                                    {hrData?.heartRateValues && (
                                        <ReplayLiveGraph
                                            points={hrData.heartRateValues}
                                            matchTimings={matchTimings}
                                            config={liveHeartRateConfig}
                                            currentTimestamp={currentTimestamp}
                                            height={mapHeight}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </IonContent>
        </IonPage>
    );
};

const ReplayPage: FC = () => {
    const { id } = useParams<{ id: string }>();
    const match = useMatch(id);

    // Video replay is a pro feature; block direct navigation for non-entitled users.
    if (!hasFeatureAccess("videoReplay")) {
        return <Redirect to={routes.upgrade} />;
    }

    if (!match) {
        return (
            <IonPage>
                <HeaderComponent showBackButton={true} />
                <IonContent className="ion-padding">
                    <IonSpinner />
                </IonContent>
            </IonPage>
        );
    }

    return (
        <FitnessDataProvider matchId={id}>
            <ReplayContent match={match} />
        </FitnessDataProvider>
    );
};

export default ReplayPage;
